import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { CartItem, CartState, DiscountDTO, Language } from 'ecosystem'
import { getProductDiscountPrice, getProductPrice, parseIntSafe, pushOrUpdate } from 'shared-utils'
import { v4 as uuidv4 } from 'uuid'
import {
  deleteCartSliceFromStorage,
  getCartSliceFromStorage,
  updateCartSliceInStorage
} from './utils'

const initialState: CartState = {
  cartLocale: null,
  cartItems: [],
  cartTotal: 0,
  cartDiscountCode: null,
  cartStateUuid: null
}

export const sumCart = (cartItems: CartItem[]) => {
  return cartItems.reduce((total, item) => {
    const value = getProductPrice(item)
    const qty = parseIntSafe(item.qty || '') ?? 1
    return total + value * qty
  }, 0)
}

const isDiscountApplicableToCart = (
  cartItems: CartItem[],
  discountCode: DiscountDTO | null
): boolean => {
  return (
    !!discountCode &&
    cartItems.some((cartItem) => !!getProductDiscountPrice(cartItem, discountCode))
  )
}

export const applyDiscountToCart = (cartItems: CartItem[], discountCode: DiscountDTO) => {
  return cartItems.map((cartItem) => {
    const { discountedPrice } = getProductDiscountPrice(cartItem, discountCode) ?? {}

    if (!discountedPrice) {
      return cartItem
    }

    return {
      ...cartItem,
      price: discountedPrice
    }
  })
}

const slice = createSlice({
  name: 'cart',
  initialState: () => {
    return getCartSliceFromStorage() ?? initialState
  },
  reducers: {
    setCartLocale: (state, { payload: cartLocale }: PayloadAction<Language>) => {
      state.cartLocale = cartLocale
      updateCartSliceInStorage(state)
      return state
    },

    addToCart: (state, { payload: cartItem }: PayloadAction<CartItem>) => {
      if (!getProductPrice(cartItem)) {
        return state
      }

      pushOrUpdate(
        state.cartItems,
        cartItem,
        (e: any) => {
          return e.id === cartItem.id
        },
        'id'
      )

      state.cartTotal = sumCart(state.cartItems)
      state.cartStateUuid = uuidv4()

      updateCartSliceInStorage(state)
      return state
    },

    removeFromCart: (state, { payload: cartItem }: PayloadAction<CartItem>) => {
      state.cartItems = state.cartItems.filter((e) => e.id !== cartItem.id)

      if (!isDiscountApplicableToCart(state.cartItems, state.cartDiscountCode)) {
        state.cartDiscountCode = null
      }

      state.cartTotal = sumCart(state.cartItems)
      state.cartStateUuid = uuidv4()

      updateCartSliceInStorage(state)
    },

    applyDiscountCode: (state, { payload: discountCode }: PayloadAction<DiscountDTO>) => {
      if (isDiscountApplicableToCart(state.cartItems, discountCode)) {
        state.cartDiscountCode = discountCode
        state.cartStateUuid = uuidv4()

        updateCartSliceInStorage(state)
      }
    },

    removeDiscountCode: (state) => {
      // Reset all prices to original and recalculate
      state.cartDiscountCode = null
      state.cartStateUuid = uuidv4()

      updateCartSliceInStorage(state)
    },

    cleanCart: () => {
      deleteCartSliceFromStorage()
      return initialState
    }
  }
})

// available actions - add more as needed, first they will have to be declared above
export const {
  setCartLocale,
  addToCart,
  removeFromCart,
  applyDiscountCode,
  removeDiscountCode,
  cleanCart
} = slice.actions

// available selectors - add more as needed
export const selectedCart = (state: { cart: CartState }) => state.cart
export const selectedCartItems = (state: { cart: CartState }) => state.cart.cartItems
export const selectedCartDiscount = (state: { cart: CartState }) => state.cart.cartDiscountCode
export const selectedCartTotal = (state: { cart: CartState }) => state.cart.cartTotal

// Selector to get cart items with current prices (already calculated)
export const selectedCartItemsWithPrices = (state: { cart: CartState }) => state.cart.cartItems

export default slice.reducer
